import {manifest, version} from '@parcel/service-worker';
/* ☝️NOTE ☝️
*
*  The import above imports an array of items as `manifest` generated by parcel.js.
*  Parcel is also nice enough to generate a `version` string for us to use to determine if cached resources should be
*  removed.
*
* */

/**
 * # Service Worker
 *
 * ## RESPONSIBILITIES
 *
 * This service worker enables the Vanilla Reader to be installed for offline use by caching all the static application
 * resources, such as scripts, images, css etc, on install.
 *
 * The worker uses a cache first strategy, with network fallback. It adds all the resources in the `manifest` to the
 * cache on install, then for each new `fetch` event it will double-check to see if it should complement the cache with
 * additional resources.
 *
 * Service Workers can be trickier than they seem, and do not always behave as described in various docs.
 *
 * A good starting point is this article on this topic on web.dev:
 * https://web.dev/offline-cookbook/
 *
 **/

/**
 * INSTALL EVENT
 **/

async function install(): Promise<void> {
    const cache = await caches.open(version);
    await cache.addAll(manifest);
}

self.addEventListener('install', (e: Event) => {
    (e as ExtendableEvent).waitUntil(install());
});

/**
 * ACTIVATE EVENT
 **/

async function activate(): Promise<void> {
    const keys = await caches.keys();
    console.log(`Service Worker with version ${version} is about to activate. Cleaning up previous cache.`);
    await Promise.all(keys.map(async (key: string) => {
        // Is the cache from another version than the current? If so delete it to keep things clean and neat.
        if (key !== version) {
            let result = await caches.delete(key);
            if (result) {
                console.log(`Deleted cache key ${key}`);
                return true;
            }
        }
        return false;
    }));

}

self.addEventListener('activate', (e: Event) => {
    return (e as ExtendableEvent).waitUntil(activate());
});

self.addEventListener('fetch', (ev) => {

    // @ts-ignore
    ev.respondWith(caches.match(ev.request, {'ignoreSearch': true}).then(cachedResponse => {

            if (cachedResponse) {

                // @ts-ignore
                return cachedResponse;
            } else {
                // @ts-ignore
                return fetch(ev.request).then(
                    (response) => {
                        let pathName = response.url.slice(response.url.lastIndexOf('/'));

                        if (!response || response.status !== 200 || response.type !== 'basic') {
                            return response;
                        }

                        if (manifest.indexOf(pathName) < 0) {
                            console.log('pathName ' + pathName + ' should not be in the cache.');
                            return response;
                        }

                        // The response is ok
                        var responseToCache = response.clone();

                        caches.open(version)
                            .then(cache => {
                                console.log('Caching response ', responseToCache);
                                // @ts-ignore
                                cache.put(ev.request, responseToCache).catch(console.warn);
                            }).catch(console.warn);

                        return response;
                    },
                );
            }
        }),
    );
});

/**
 *
 * MESSAGE EVENT
 *
 **/

self.addEventListener('message', async (ev: MessageEvent<VanillaServiceWorkerEvent>) => {
    switch (ev.data.eventName) {
        case VanillaServiceWorkerEventName.APP_PWA_SKIP_WAITING_INTENT:

            // @ts-ignore
            await self.skipWaiting();

            // @ts-ignore
            await self.clients.claim();

            console.log('Service Worker updated to version ' + version);
            break;
    }
});

export interface VanillaServiceWorkerEvent {
    eventName: VanillaServiceWorkerEventName
}

export enum VanillaServiceWorkerEventName {
    'APP_PWA_SKIP_WAITING_INTENT' = 'APP_PWA_SKIP_WAITING_INTENT'
}
